import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Swal from 'sweetalert2'
import moment from 'moment'
import { editSubscriptionPlans, viewSubscriptionPlanDetails } from '../../slice/subscriptionPlanSlice'

// Validation Schema for Edit
const editSubscriptionPlanSchema = yup.object().shape({
    name: yup
        .string()
        .required('Plan name is required')
        .min(3, 'Plan name must be at least 3 characters')
        .max(100, 'Plan name cannot exceed 100 characters'),

    description: yup
        .string()
        .required('Description is required')
        .min(10, 'Description must be at least 10 characters')
        .max(500, 'Description cannot exceed 500 characters'),

    duration_type: yup
        .string()
        .required('Duration type is required')
        .oneOf(['free', 'annually'], 'Please select a valid duration type'),

    duration_days: yup
        .number()
        .required('Duration in days is required')
        .min(0, 'Duration cannot be negative')
        .max(36500, 'Duration cannot exceed 36500 days')
        .typeError('Duration must be a number'),

    price: yup
        .number()
        .required('Price is required')
        .min(0, 'Price cannot be negative')
        .typeError('Price must be a number'),

    apple_product_id: yup
        .string()
        .nullable()
        .transform((v) => (v === '' ? null : v))
        .matches(/^[a-zA-Z0-9._-]+$/, {
            message: 'Apple Product ID can only contain letters, numbers, dots, hyphens, and underscores',
            excludeEmptyString: true,
        }),

    google_product_id: yup
        .string()
        .nullable()
        .transform((v) => (v === '' ? null : v))
        .matches(/^[a-zA-Z0-9._-]+$/, {
            message: 'Google Product ID can only contain letters, numbers, dots, hyphens, and underscores',
            excludeEmptyString: true,
        }),
})

// ── Bilingual Features Manager ───────────────────────────────────────────────
const FeaturesManager = ({ features, onChange }) => {
    const [newFeatureEn, setNewFeatureEn] = useState('')
    const [newFeatureAr, setNewFeatureAr] = useState('')
    const [inputError, setInputError] = useState('')

    const handleAdd = () => {
        const trimEn = newFeatureEn.trim()
        const trimAr = newFeatureAr.trim()

        if (!trimEn && !trimAr) {
            setInputError('Please enter at least one feature (English or Arabic).')
            return
        }

        setInputError('')
        onChange({
            en: [...features.en, trimEn],
            ar: [...features.ar, trimAr],
        })
        setNewFeatureEn('')
        setNewFeatureAr('')
    }

    const handleDelete = (index) => {
        onChange({
            en: features.en.filter((_, i) => i !== index),
            ar: features.ar.filter((_, i) => i !== index),
        })
    }

    const handleEdit = (index, lang, value) => {
        const updated = { ...features }
        updated[lang] = updated[lang].map((item, i) => (i === index ? value : item))
        onChange(updated)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleAdd()
        }
    }

    return (
        <div>
            {/* Existing features list */}
            {features.en.length > 0 && (
                <div className="mb-3">
                    {/* Table header */}
                    <div className="row g-2 mb-2 px-1">
                        <div className="col-5">
                            <small className="fw-semibold text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                🇬🇧 English
                            </small>
                        </div>
                        <div className="col-5">
                            <small className="fw-semibold text-muted text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                                🇦🇪 Arabic
                            </small>
                        </div>
                        <div className="col-2" />
                    </div>

                    {features.en.map((enItem, index) => (
                        <div
                            key={index}
                            className="row g-2 mb-2 align-items-center"
                            style={{
                                background: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '8px 4px',
                                margin: '0 0 8px 0',
                                border: '1px solid #e9ecef'
                            }}
                        >
                            {/* English input */}
                            <div className="col-5">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={enItem}
                                    onChange={(e) => handleEdit(index, 'en', e.target.value)}
                                    placeholder="English feature"
                                    style={{ fontSize: '13px' }}
                                />
                            </div>

                            {/* Arabic input */}
                            <div className="col-5">
                                <input
                                    type="text"
                                    className="form-control form-control-sm text-end"
                                    value={features.ar[index] || ''}
                                    onChange={(e) => handleEdit(index, 'ar', e.target.value)}
                                    placeholder="ميزة بالعربية"
                                    dir="rtl"
                                    style={{ fontSize: '13px' }}
                                />
                            </div>

                            {/* Delete */}
                            <div className="col-2 text-center">
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => handleDelete(index)}
                                    title="Remove feature"
                                    style={{
                                        color: '#dc3545',
                                        background: 'transparent',
                                        border: '1px solid #dc354540',
                                        borderRadius: '6px',
                                        padding: '3px 8px',
                                        fontSize: '13px',
                                        lineHeight: 1,
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty state */}
            {features.en.length === 0 && (
                <div
                    className="text-center py-3 mb-3"
                    style={{
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1.5px dashed #dee2e6',
                        color: '#adb5bd',
                        fontSize: '13px'
                    }}
                >
                    No features added yet. Use the fields below to add your first feature.
                </div>
            )}

            {/* Add new feature row */}
            <div
                style={{
                    background: '#eaf4ff',
                    borderRadius: '8px',
                    border: '1.5px dashed #90c5f5',
                    padding: '12px',
                }}
            >
                <small className="fw-semibold text-primary mb-2 d-block" style={{ fontSize: '12px' }}>
                    + Add New Feature
                </small>
                <div className="row g-2 align-items-start">
                    <div className="col-5">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g., Unlimited sessions"
                            value={newFeatureEn}
                            onChange={(e) => { setNewFeatureEn(e.target.value); setInputError('') }}
                            onKeyDown={handleKeyDown}
                            style={{ fontSize: '13px' }}
                        />
                        <small className="text-muted" style={{ fontSize: '11px' }}>English</small>
                    </div>
                    <div className="col-5">
                        <input
                            type="text"
                            className="form-control form-control-sm text-end"
                            placeholder="مثلاً: جلسات غير محدودة"
                            value={newFeatureAr}
                            onChange={(e) => { setNewFeatureAr(e.target.value); setInputError('') }}
                            onKeyDown={handleKeyDown}
                            dir="rtl"
                            style={{ fontSize: '13px' }}
                        />
                        <small className="text-muted" style={{ fontSize: '11px' }}>Arabic</small>
                    </div>
                    <div className="col-2">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm w-100"
                            onClick={handleAdd}
                            style={{ fontSize: '13px', borderRadius: '6px' }}
                        >
                            Add
                        </button>
                    </div>
                </div>
                {inputError && (
                    <small className="text-danger d-block mt-1" style={{ fontSize: '12px' }}>
                        {inputError}
                    </small>
                )}
                <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                    Press <kbd style={{ fontSize: '10px' }}>Enter</kbd> or click Add. Both fields are optional but at least one is required per row.
                </small>
            </div>
        </div>
    )
}

// ── Main Component ────────────────────────────────────────────────────────────
const EditSubscriptionPlan = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const planId = location.state?.planId
    const { subscriptionPlanDetail, loading } = useSelector(state => state.subscriptionPlan)

    const [isSubmitting, setIsSubmitting] = useState(false)

    // Managed outside react-hook-form since it's a dynamic array
    const [features, setFeatures] = useState({ en: [], ar: [] })
    const [featuresError, setFeaturesError] = useState('')

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue
    } = useForm({
        resolver: yupResolver(editSubscriptionPlanSchema),
        defaultValues: {
            name: '',
            description: '',
            duration_type: '',
            duration_days: '',
            price: '',
            apple_product_id: '',
            google_product_id: '',
        }
    })

    const durationType = watch('duration_type')
    const price = watch('price')
    const durationDays = watch('duration_days')

    // Fetch plan details on mount
    useEffect(() => {
        if (!planId) {
            navigate('/subscriptions')
            return
        }
        dispatch(viewSubscriptionPlanDetails({ id: planId }))
    }, [planId, dispatch, navigate])

    // Populate form when data loads
    useEffect(() => {
        if (subscriptionPlanDetail) {
            setValue('name', subscriptionPlanDetail.name || '')
            setValue('description', subscriptionPlanDetail.description || '')
            setValue('duration_type', subscriptionPlanDetail.duration_type || '')
            setValue('duration_days', subscriptionPlanDetail.duration_days || '')
            setValue('price', subscriptionPlanDetail.price || '')
            setValue('apple_product_id', subscriptionPlanDetail.apple_product_id || '')
            setValue('google_product_id', subscriptionPlanDetail.google_product_id || '')

            // Populate features from API data
            if (subscriptionPlanDetail.features) {
                const apiFeatures = subscriptionPlanDetail.features
                if (apiFeatures.en && apiFeatures.ar) {
                    // Already in { en: [...], ar: [...] } format
                    setFeatures({
                        en: Array.isArray(apiFeatures.en) ? apiFeatures.en : [],
                        ar: Array.isArray(apiFeatures.ar) ? apiFeatures.ar : [],
                    })
                } else if (Array.isArray(apiFeatures)) {
                    // Legacy format: plain array of strings → treat as English only
                    setFeatures({
                        en: apiFeatures,
                        ar: apiFeatures.map(() => ''),
                    })
                }
            }
        }
    }, [subscriptionPlanDetail, setValue])

    const handleDurationTypeChange = (e) => {
        const type = e.target.value
        setValue('duration_type', type)
        setValue('duration_days', type === 'annually' ? 365 : 0)
    }

    // Calculate price per day
    const getPricePerDay = () => {
        if (price && durationDays && parseFloat(price) > 0 && parseFloat(durationDays) > 0) {
            return (parseFloat(price) / parseFloat(durationDays)).toFixed(2)
        }
        return null
    }

    const onSubmit = async (data) => {
        // Validate features have at least one entry for premium plans
        if (data.duration_type === 'annually' && features.en.length === 0) {
            setFeaturesError('Please add at least one feature for a paid plan.')
            return
        }
        setFeaturesError('')
        setIsSubmitting(true)

        try {
            const payload = {
                id: planId,
                name: data.name,
                description: data.description,
                duration_type: data.duration_type,
                duration_days: Number(data.duration_days),
                price: Number(data.price),
                apple_product_id: data.apple_product_id || null,
                google_product_id: data.google_product_id || null,
                features,   // { en: [...], ar: [...] }
            }

            const response = await dispatch(editSubscriptionPlans(payload))

            if (response?.payload?.code === 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Subscription plan updated successfully',
                    timer: 2000,
                    showConfirmButton: false,
                })

                setTimeout(() => navigate('/subscriptions'), 2000)
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response?.payload?.message || 'Failed to update subscription plan',
                })
            }

        } catch (error) {
            console.error(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.message || 'Something went wrong',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    if (!subscriptionPlanDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/subscriptions')}
                >
                    Go Back to Subscription Plans
                </button>
            </div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Edit Subscription Plan</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/subscriptions')}
                    style={{
                        padding: '8px 20px',
                        fontSize: '14px',
                        borderRadius: '6px'
                    }}
                >
                    ← Back to List
                </button>
            </div>

            {/* Status Alert */}
            {subscriptionPlanDetail?.is_deleted === 1 && (
                <div className="alert alert-danger mb-4" style={{ borderRadius: '8px' }}>
                    <i className="fe-trash-2 me-2"></i>
                    <strong>This subscription plan has been deleted and is not available for purchase.</strong>
                </div>
            )}

            {subscriptionPlanDetail?.is_active === 0 && subscriptionPlanDetail?.is_deleted === 0 && (
                <div className="alert alert-warning mb-4" style={{ borderRadius: '8px' }}>
                    <i className="fe-alert-circle me-2"></i>
                    <strong>This subscription plan is currently inactive and not available for purchase.</strong>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    {/* Plan Information */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                <i className="fe-info me-2"></i>
                                Plan Information
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Plan Name */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Plan Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            placeholder="e.g., Premium Annual"
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">{errors.name.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Enter a clear, descriptive name for the plan
                                        </small>
                                    </div>

                                    {/* Duration Type */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Duration Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select ${errors.duration_type ? 'is-invalid' : ''}`}
                                            {...register('duration_type')}
                                            onChange={handleDurationTypeChange}
                                        >
                                            <option value="">Select duration type</option>
                                            <option value="free">Free</option>
                                            <option value="annually">Annually</option>
                                        </select>
                                        {errors.duration_type && (
                                            <div className="invalid-feedback">{errors.duration_type.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Select the billing cycle type
                                        </small>
                                    </div>

                                    {/* Description */}
                                    <div className="col-12">
                                        <label className="form-label">
                                            Description <span className="text-danger">*</span>
                                        </label>
                                        <textarea
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            placeholder="Describe the features and benefits of this plan"
                                            rows="3"
                                            {...register('description')}
                                        />
                                        {errors.description && (
                                            <div className="invalid-feedback">{errors.description.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Minimum 10 characters, maximum 500 characters
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Duration & Pricing */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                <i className="fe-clock me-2"></i>
                                Duration & Pricing
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Duration Days */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Duration (Days) <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            className={`form-control ${errors.duration_days ? 'is-invalid' : ''}`}
                                            placeholder="Enter number of days"
                                            {...register('duration_days')}
                                            min="0"
                                            max="36500"
                                        />
                                        {errors.duration_days && (
                                            <div className="invalid-feedback">{errors.duration_days.message}</div>
                                        )}
                                        <small className="text-muted">
                                            {durationType === 'annually' && 'Suggested: 365 days'}
                                            {durationType === 'free' && '0 days (no expiry)'}
                                            {!durationType && 'Total number of days the plan is valid'}
                                        </small>
                                    </div>

                                    {/* Price */}
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Price (AED) <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">AED</span>
                                            <input
                                                type="number"
                                                className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                {...register('price')}
                                                min="0"
                                                step="0.01"
                                            />
                                            {errors.price && (
                                                <div className="invalid-feedback">{errors.price.message}</div>
                                            )}
                                        </div>
                                        <small className="text-muted">
                                            Enter 0 for a free plan
                                        </small>
                                    </div>

                                    {/* Price Per Day Display */}
                                    {getPricePerDay() && (
                                        <div className="col-12">
                                            <div className="alert alert-info mb-0" style={{ borderRadius: '6px' }}>
                                                <strong>Price per day:</strong> AED {getPricePerDay()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Plan Features (Bilingual) ────────────────────────── */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light border-0 d-flex align-items-center justify-content-between" style={{ borderRadius: '8px 8px 0 0' }}>
                                <span className="fw-semibold">
                                    <i className="fe-list me-2"></i>Plan Features
                                </span>
                                <span
                                    className="badge"
                                    style={{
                                        background: features.en.length > 0 ? '#198754' : '#6c757d',
                                        color: '#fff',
                                        fontSize: '12px',
                                        borderRadius: '20px',
                                        padding: '4px 10px'
                                    }}
                                >
                                    {features.en.length} feature{features.en.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="card-body p-4">
                                <FeaturesManager features={features} onChange={setFeatures} />
                                {featuresError && (
                                    <div className="text-danger mt-2" style={{ fontSize: '13px' }}>
                                        {featuresError}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* App Store Configuration */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                <i className="fe-smartphone me-2"></i>
                                App Store Configuration
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Apple Product ID */}
                                    <div className="col-md-6">
                                        <label className="form-label">Apple Product ID</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.apple_product_id ? 'is-invalid' : ''}`}
                                            placeholder="com.myapp.premium.yearly"
                                            {...register('apple_product_id')}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '13px'
                                            }}
                                        />
                                        {errors.apple_product_id && (
                                            <div className="invalid-feedback">{errors.apple_product_id.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Product ID from Apple App Store Connect
                                        </small>
                                    </div>

                                    {/* Google Product ID */}
                                    <div className="col-md-6">
                                        <label className="form-label">Google Product ID</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.google_product_id ? 'is-invalid' : ''}`}
                                            placeholder="premium_yearly_001"
                                            {...register('google_product_id')}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '13px'
                                            }}
                                        />
                                        {errors.google_product_id && (
                                            <div className="invalid-feedback">{errors.google_product_id.message}</div>
                                        )}
                                        <small className="text-muted">
                                            Product ID from Google Play Console
                                        </small>
                                    </div>

                                    {/* Info Alert */}
                                    <div className="col-12">
                                        <div className="alert alert-warning mb-0" style={{ borderRadius: '6px' }}>
                                            <strong>Important:</strong> Make sure these Product IDs match exactly with the ones configured in Apple App Store Connect and Google Play Console.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Preview Card ─────────────────────────────────────── */}
                    {watch('name') && watch('price') !== '' && durationType && (
                        <div className="col-12 mb-4">
                            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                    <i className="fe-eye me-2"></i>Preview
                                </div>
                                <div className="card-body p-4">
                                    <div className="text-center">
                                        <div style={{
                                            display: 'inline-block',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            padding: '40px 60px',
                                            borderRadius: '16px',
                                            color: 'white',
                                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                            minWidth: '350px',
                                            textAlign: 'left',
                                        }}>
                                            <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px' }}>
                                                {watch('name')}
                                            </h3>

                                            <div className="mb-3">
                                                {watch('price') == 0 ? (
                                                    <div style={{ fontSize: '40px', fontWeight: '700' }}>FREE</div>
                                                ) : (
                                                    <div>
                                                        <span style={{ fontSize: '20px', opacity: 0.9 }}>AED </span>
                                                        <span style={{ fontSize: '48px', fontWeight: '700' }}>
                                                            {parseFloat(watch('price') || 0).toFixed(0)}
                                                        </span>
                                                        <span style={{ fontSize: '18px', opacity: 0.9 }}> / {durationType}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {watch('description') && (
                                                <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '16px' }}>
                                                    {watch('description')}
                                                </p>
                                            )}

                                            {/* Feature preview list */}
                                            {features.en.length > 0 && (
                                                <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0' }}>
                                                    {features.en.map((f, i) => (
                                                        <li key={i} style={{ fontSize: '13px', marginBottom: '6px', opacity: 0.95 }}>
                                                            <span style={{ marginRight: '8px' }}>✓</span>{f}
                                                            {features.ar[i] && (
                                                                <span style={{ marginLeft: '8px', opacity: 0.75, direction: 'rtl', display: 'inline-block' }}>
                                                                    / {features.ar[i]}
                                                                </span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Current Status Info */}
                    <div className="col-12 mb-4">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                                Current Status
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <small className="text-muted d-block mb-1">Status</small>
                                        <div>
                                            {subscriptionPlanDetail?.is_active === 1 ? (
                                                <span className="badge bg-success">Active</span>
                                            ) : (
                                                <span className="badge bg-secondary">Inactive</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-3">
                                        <small className="text-muted d-block mb-1">Deletion Status</small>
                                        <div>
                                            {subscriptionPlanDetail?.is_deleted === 1 ? (
                                                <span className="badge bg-danger">Deleted</span>
                                            ) : (
                                                <span className="badge bg-success">Not Deleted</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-3">
                                        <small className="text-muted d-block mb-1">Created At</small>
                                        <strong>
                                            {subscriptionPlanDetail?.createdAt
                                                ? moment(subscriptionPlanDetail.createdAt).format('DD MMM YYYY')
                                                : 'NA'}
                                        </strong>
                                    </div>

                                    <div className="col-md-3">
                                        <small className="text-muted d-block mb-1">Last Updated</small>
                                        <strong>
                                            {subscriptionPlanDetail?.updatedAt
                                                ? moment(subscriptionPlanDetail.updatedAt).format('DD MMM YYYY')
                                                : 'NA'}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="d-flex gap-3 justify-content-end">
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => navigate('/subscriptions')}
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                        style={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '6px',
                            minWidth: '120px'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            'Update Plan'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}

export default EditSubscriptionPlan